import { useEffect, useRef } from 'react'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import {
  buildCurveLabel,
  generateModelShades,
  MODEL_BASE_COLORS,
} from './powerCurveUtils'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'

// kWh → kW, MWh → MW, anything else kept as-is
const resolveUnit = raw => {
  if (raw === 'MWh' || raw === 'MW') return 'MW'
  return 'kW'
}

const PowerCurveChart = ({ chartName, type, curves, height = 550 }) => {
  const containerRef = useRef(null)

  useEffect(() => {
    if (!curves || curves.length === 0) return

    const rawUnit = loadStateFromLocalStorage('unit') ?? 'kW'
    const unit = resolveUnit(rawUnit)
    const isMW = type === 'wind' && unit === 'MW'

    am5.array.each(am5.registry.rootElements, root => {
      if (root?.dom?.id === chartName) root.dispose()
    })

    const root = am5.Root.new(chartName)
    root._logo.dispose()

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelY: 'zoomXY',
        layout: root.verticalLayout,
      }),
    )

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 50 }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    )
    xAxis.children.push(
      am5.Label.new(root, {
        text: type === 'wind' ? 'Wind Speed (m/s)' : 'Radiation (W/m²)',
        x: am5.p50,
        centerX: am5.p50,
      }),
    )

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    )
    yAxis.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: type === 'wind' ? `Output Power (${unit})` : 'Output Power Ratio',
        y: am5.p50,
        centerX: am5.p50,
      }),
    )

    const xField = type === 'wind' ? 'wind_speed_start' : 'radiation_start'
    const yField = type === 'wind' ? 'output_power' : 'output_power_ratio'

    const prepareData = data => {
      if (!isMW) return data
      return data.map(d => ({
        ...d,
        output_power: d.output_power != null ? d.output_power * 0.001 : null,
      }))
    }

    // Group curves by model so each model gets its own color family
    const modelGroups = {}
    curves.forEach(curve => {
      const mn = curve._modelName ?? 'unknown'
      if (!modelGroups[mn]) modelGroups[mn] = []
      modelGroups[mn].push(curve)
    })

    Object.entries(modelGroups).forEach(([mn, modelCurves]) => {
      const baseHex = MODEL_BASE_COLORS[mn] ?? 0x368ed8
      const shades = generateModelShades(baseHex, modelCurves.length)

      modelCurves.forEach((curve, i) => {
        const label = buildCurveLabel(mn, curve)
        const color = am5.color(shades[i])

        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: label,
            xAxis,
            yAxis,
            valueXField: xField,
            valueYField: yField,
            stroke: color,
            fill: color,
            connect: true,
            tooltip: am5.Tooltip.new(root, {
              pointerOrientation: 'left',
              labelText:
                type === 'wind'
                  ? `{name}  WS: {valueX.formatNumber('#.#')}  OP: {valueY.formatNumber('${isMW ? '#.#' : '#.'}')} ${unit}`
                  : `{name}  Rad: {valueX.formatNumber('#.')}  Ratio: {valueY.formatNumber('#.###')}`,
            }),
          }),
        )

        series.strokes.template.setAll({ strokeWidth: 2 })
        series.data.setAll(prepareData(curve.data))
      })
    })

    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'zoomXY',
        xAxis,
        yAxis,
      }),
    )

    const legend = chart.children.push(
      am5.Legend.new(root, {
        layout: root.gridLayout,
        centerX: am5.percent(50),
        x: am5.percent(50),
      }),
    )
    legend.data.setAll(chart.series.values)

    return () => {
      root.dispose()
    }
  }, [chartName, type, curves])

  return (
    <div
      ref={containerRef}
      id={chartName}
      style={{ width: '100%', height: `${height}px`, marginLeft: -18 }}
    />
  )
}

export default PowerCurveChart
